import { getLocalTimeZone, today } from '@internationalized/date'

import { CalculadorPrazoPrice } from '@/domain/simulador/factories/concrete/horizontal/price/calculador-prazo-price'
import { GruposParcelasApiPriceMapper } from '@/domain/simulador/factories/concrete/horizontal/price/grupos-parcelas-api-price.mapper'
import {
  RotinaArquivoPrice,
  type TSimulacaoCalculoPrice,
} from '@/domain/simulador/factories/concrete/horizontal/price/rotina-arquivo-price'
import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import type { TSimulacaoCalculoPrazo } from '@/domain/simulador/factories/types/simulacao-calculo'
import type { TParametrosDinamicosSimulacaoSimulador, TSimulacaoSchema } from '@/domain/simulador/types/simulacao.type'
import { parametrosRotinaArquivo } from '@/features/simulador/brasil/__mocks__/parametros'
import { CalculadorFluxoDePagamentoBase } from '@/features/simulador/brasil/domain/calculador-fluxo-pagamento-base'
import { GerenciadorEntradasPrice } from '@/features/simulador/brasil/domain/gerenciador-entradas-price'
import { GerenciadorIntermediariasPrice } from '@/features/simulador/brasil/domain/gerenciador-intermediarias-price'
import { GerenciadorMensaisPrice } from '@/features/simulador/brasil/domain/gerenciador-mensais-price'
import type { TGetSimulacaoDuplicarResponse } from '@/types/simulacao'

type TSimulacaoDuplicarPrice = TGetSimulacaoDuplicarResponse & {
  valorProposto: number
  prazoDeParcelamentoPrice: number
}

type TSimulacaoPrice = {
  condicaoDePrecoId: string
  tabelaDePrecoId: string
  bloco: { idExterno: string; nome: string }
  produto: { idExterno: string; nome: string; metragem?: string }
  dataSaldoCondicaoPreco: string
  dataPrevisaoDeEntregaDoBloco: string
  tipoDeAmortizacao: 'Price'
  valorProposto: number
  prazoDeParcelamentoPrice: number
}

export class SimulacaoPrice {
  protected readonly dataSimulacao: string = today(getLocalTimeZone()).toString()
  readonly mensais = new GerenciadorMensaisPrice()
  readonly entradas = new GerenciadorEntradasPrice()
  readonly intermediarias = new GerenciadorIntermediariasPrice()

  constructor(private readonly rotinaArquivo: RotinaArquivoPrice = new RotinaArquivoPrice(parametrosRotinaArquivo)) {}

  protected criarSimulacaoBase(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoPrice {
    const { condicaoDePreco, tabelaDePreco, bloco, produto } = parametrosDinamicos

    return {
      condicaoDePrecoId: condicaoDePreco.id,
      tabelaDePrecoId: tabelaDePreco.id,
      bloco: { idExterno: String(bloco.idExterno), nome: bloco.nome },
      produto: {
        ...produto,
        idExterno: String(produto.idExterno),
      },
      dataSaldoCondicaoPreco: condicaoDePreco.dataSaldo,
      dataPrevisaoDeEntregaDoBloco: condicaoDePreco.previsaoDeEntregaDoBloco,
      tipoDeAmortizacao: 'Price',
      valorProposto: tabelaDePreco.valorBase,
      prazoDeParcelamentoPrice: condicaoDePreco.opcoesDeParcelamentoPrice?.[0] ?? 0,
    }
  }

  protected criarGruposParcela(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador) {
    const { condicaoDePreco, tabelaDePreco } = parametrosDinamicos

    const entradas = this.entradas.criarGrupoInicial(
      [
        condicaoDePreco.dataSinal1,
        condicaoDePreco.dataSinal2,
        condicaoDePreco.dataSinal3,
        condicaoDePreco.dataSinal4,
      ].filter((dataSinal): dataSinal is string => Boolean(dataSinal)),
      condicaoDePreco.percentualSinal ?? 0,
      { data: condicaoDePreco.dataAto, percentualAto: condicaoDePreco.percentualAto ?? 0 },
      tabelaDePreco.valorBase,
    )

    const mensais = this.mensais.criarGrupoInicial(
      tabelaDePreco.valorBaseMensais,
      tabelaDePreco.valorBase,
      condicaoDePreco.dataPrimeiraMensal,
      condicaoDePreco.opcoesDeParcelamentoPrice?.[0] ?? 0,
      condicaoDePreco.percentualMensais ?? 0,
    )

    const hasEntradas = Boolean(entradas?.grupos?.length)
    const hasMensais = Boolean(mensais)

    const intermediarias =
      hasEntradas && hasMensais
        ? this.intermediarias.criarGrupoInicial(
            condicaoDePreco.dataDasIntermediarias,
            tabelaDePreco.valorBaseIntermediarias,
            condicaoDePreco.percentualIntermediarias ?? 0,
            tabelaDePreco.valorBase,
          )
        : undefined

    return {
      entradas,
      mensais,
      intermediarias,
    }
  }

  criar = (parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSchema<'Price'> => {
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    const grupos = this.criarGruposParcela(parametrosDinamicos)

    return {
      id: crypto.randomUUID(),
      ...simulacaoBase,
      ...grupos,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
      dataSimulacao: this.dataSimulacao,
      percentualJurosAnualPrice: parametrosDinamicos.tabelaDePreco.percentualJurosAnualPrice ?? 0,
    }
  }

  duplicar = (
    simulacao: TGetSimulacaoDuplicarResponse,
    parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador,
  ): TSimulacaoSchema<'Price'> => {
    const { valorProposto, prazoDeParcelamentoPrice } = simulacao as TSimulacaoDuplicarPrice
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    const { entradas, mensais, intermediarias } = new GruposParcelasApiPriceMapper(
      simulacao,
      valorProposto,
    ).buildGruposParcelas()

    return {
      id: crypto.randomUUID(),
      simulacaoAnteriorId: simulacao.id,
      dataSimulacao: this.dataSimulacao,
      ...simulacaoBase,
      valorProposto,
      entradas,
      mensais,
      intermediarias,
      prazoDeParcelamentoPrice,
      percentualJurosAnualPrice: parametrosDinamicos.tabelaDePreco.percentualJurosAnualPrice ?? 0,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  recalcular = (parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador) => {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)

    return {
      ...simulacaoBase,
      ...grupos,
    }
  }

  calcularFluxoDePagamento(simulacao: TSimulacaoSchema<'Price'>): CalculadorFluxoDePagamentoBase {
    return new CalculadorFluxoDePagamentoBase(
      simulacao.entradas?.grupos,
      simulacao.mensais?.parcelas,
      simulacao.intermediarias?.grupos,
    )
  }

  calcularValoresTotais() {
    return null
  }

  calcularPrazo(simulacao: TSimulacaoCalculoPrazo) {
    return new CalculadorPrazoPrice(simulacao)
  }

  calcularRotinaArquivo(grupos: TSimulacaoCalculoPrice, parametros: TParametrosRotinaArquivo) {
    return new RotinaArquivoPrice(parametros).calcular(grupos)
  }
}
